// src/utils/firebaseUtils.js

import {
    collection,
    getDocs,
    query,
    where,
    limit as fsLimit,
    doc,
    setDoc,
    addDoc,
    serverTimestamp,
    getDoc,
    Timestamp,
    updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  startOfDay, format, parseISO, 
  addMinutes
} from 'date-fns';

/**
 * Helper function to assign a numeric priority based on external_website.
 * Lower number => higher priority in sorting.
 * 1 => reddit
 * 2 => youtube / tiktok
 * 3 => everything else
 */
function getSourcePriority(website = '') {
    const lower = website.toLowerCase();
    if (lower.includes('reddit')) {
        return 1;
    } else if (lower.includes('youtube') || lower.includes('tiktok')) {
        return 2;
    }
    return 3;
}

/**
 * Fetch reviews from Firestore (example, unchanged)
 */
export const fetchReviews = async () => {
    try {
        const querySnapshot = await getDocs(collection(db, 'reviews'));
        return querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        //console.error('Error fetching reviews:', error);
        return [];
    }
};

/**
 * Fetch clinics from Firestore whose clinic_only_name is in the given array.
 * (Unchanged)
 */
export const fetchClinicsByNames = async (clinicNames = []) => {
    if (!Array.isArray(clinicNames) || clinicNames.length === 0) {
        return [];
    }
    try {
        const clinicsRef = collection(db, 'clinics');
        const q = query(clinicsRef, where('clinic_only_name', 'in', clinicNames));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        //console.error('Error fetching clinics by names:', error);
        return [];
    }
};

/**
 * Fetch review documents by Firestore doc IDs (review_id).
 * (Unchanged)
 */
export const fetchReviewsByIds = async (reviewIds = []) => {
    if (!Array.isArray(reviewIds) || reviewIds.length === 0) {
        return [];
    }

    // If you have more than 10, consider slicing or chunking
    const limitedIds = reviewIds.slice(0, 20);

    try {
        const reviewsRef = collection(db, 'reviews');
        const q = query(reviewsRef, where('__name__', 'in', limitedIds));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => ({
            id: doc.id, // doc.id == review_id
            ...doc.data(),
        }));
    } catch (error) {
        //console.error('Error fetching reviews by doc IDs:', error);
        return [];
    }
};

/**
 * Fetch up to `maxItems` reviews from Firestore for a particular clinic
 * where user_satisfaction > `threshold`.
 * Then prioritize: (1) reddit, (2) youtube/tiktok, (3) others.
 * If Firestore complains about needing an index, we catch that error
 * and fallback to a single-condition fetch, then filter in memory.
 */
export const fetchReviewsForClinicSatisfaction = async (
    clinicName,
    threshold = 3,
    maxItems = 10
) => {
    if (!clinicName || typeof clinicName !== 'string') {
        return [];
    }

    try {
        // Attempt the direct compound query
        const reviewsRef = collection(db, 'reviews');
        const q = query(
            reviewsRef,
            where('clinic_only_name', '==', clinicName),
            where('user_satisfaction', '>', threshold),
            fsLimit(maxItems * 3) // fetch a few extras so we can reorder
        );

        const querySnapshot = await getDocs(q);

        // Convert them to array
        let docsAll = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));

        // In-memory reorder by source priority
        docsAll.sort((a, b) => {
            const prioA = getSourcePriority(a.external_website);
            const prioB = getSourcePriority(b.external_website);
            return prioA - prioB;
        });

        // Then limit to maxItems
        return docsAll.slice(0, maxItems);

    } catch (error) {
        // Fallback: fetch only by clinic_name, then filter in memory
        try {
            const reviewsRef = collection(db, 'reviews');
            const qSingle = query(
                reviewsRef,
                where('clinic_only_name', '==', clinicName),
                fsLimit(30) // fetch a few extras, then filter
            );
            const querySnapshot = await getDocs(qSingle);
            let docsAll = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            // Filter in memory for user_satisfaction > threshold
            docsAll = docsAll.filter((d) => {
                const val = parseFloat(d.user_satisfaction);
                return !isNaN(val) && val > threshold;
            });

            // Reorder by source priority
            docsAll.sort((a, b) => {
                const prioA = getSourcePriority(a.external_website);
                const prioB = getSourcePriority(b.external_website);
                return prioA - prioB;
            });

            // Limit to maxItems
            return docsAll.slice(0, maxItems);

        } catch (fallbackError) {
            //console.error('Fallback single-condition fetch also failed:', fallbackError);
            return [];
        }
    }
};

/**
 * Save a recommendation submission to Firestore.
 * The submissionData object should contain:
 *  - formData: all inputs sent to the cloud function (including the logged in user's email)
 *  - results_table: the entire results_table that was returned
 *  - clinic_justification_reviews_tables: the entire list of tables
 *  - performance_table: the entire performance table
 */
export const saveRecommendationSubmission = async (submissionData) => {
    try {
        const userEmail = submissionData.formData.email;
        if (!userEmail) {
            return;
        }
        // Reference to the user document in recommendations_frontend collection
        const userDocRef = doc(db, "recommendations_frontend", userEmail);
        await setDoc(userDocRef, {}, { merge: true });
        
        
        // Reference to the subcollection "submissions"
        const submissionsRef = collection(userDocRef, "submissions");
        await setDoc(doc(submissionsRef, submissionData.formData.recommendation_id), submissionData);

    } catch (error) {
        //console.error("Error saving recommendation submission:", error);
    }
};

/**
 * Save a lead submission to Firestore.
 * The leadData object should contain:
 *  - name: user's name
 *  - phoneNumber: user's phone number (including country code)
 *  - (Optional) submittedAt: a client-side timestamp, if desired.
 * This function will add a server-generated timestamp as 'createdAt'.
 */
export const saveLeadSubmission = async (leadData) => {
    try {
        await addDoc(collection(db, "leads"), {
            ...leadData,
            createdAt: serverTimestamp(),
        });
    } catch (error) {
        console.error("Error saving lead submission:", error);
    }
};

/**
 * Fetch a recommendation by ID from Firestore.
 * Returns the full recommendation data in the same format as the cloud function would
 * @param {string} recommendationId - The recommendation ID to fetch
 * @returns {Object|null} The recommendation data or null if not found
 */
export const fetchRecommendationById = async (recommendationId) => {
    if (!recommendationId) return null;
    
    try {
        // First try to find which user has this recommendation
        const usersRef = collection(db, "recommendations_frontend");
        const allUsersSnap = await getDocs(usersRef);
        
        for (const userDoc of allUsersSnap.docs) {
            const userEmail = userDoc.id;
            const submissionsRef = collection(db, "recommendations_frontend", userEmail, "submissions");
            
            // Check if this recommendation ID exists in this user's submissions
            const submissionDoc = doc(submissionsRef, recommendationId);
            const submissionSnap = await getDoc(submissionDoc);
            
            if (submissionSnap.exists()) {
                // Found the recommendation
                const submissionData = submissionSnap.data();
                
                // Return the result in the format the cloud function would provide
                return {
                    results_table: submissionData.results_table,
                    clinic_justification_reviews_tables: submissionData.clinic_justification_reviews_tables || [],
                    performance: submissionData.performance || null,
                    formData: submissionData.formData || { name: "" }
                };
            }
        }
        
        // If we reach here, recommendation was not found
        console.error(`Recommendation with ID ${recommendationId} not found`);
        return null;
    } catch (error) {
        console.error("Error fetching recommendation by ID:", error);
        return null;
    }
};

/**
 * Fetch form data by recommendation ID from the recommendations_form_data collection
 * @param {string} recommendationId - The recommendation ID to fetch form data for
 * @returns {Object|null} The form data or null if not found
 */
export const fetchFormDataById = async (recommendationId) => {
    if (!recommendationId) return null;
    
    try {
        const formDataRef = doc(db, "recommendations_form_data", recommendationId);
        const formDataSnap = await getDoc(formDataRef);
        
        if (formDataSnap.exists()) {
            return formDataSnap.data();
        }
        
        return null;
    } catch (error) {
        console.error("Error fetching form data by ID:", error);
        return null;
    }
};

/**
 * Fetch calendar availability rules and bookings from Firestore
 * Returns calendar rules, special dates, and booking labels
 * Firestore automatically converts UTC timestamps to local timezone
 * @returns {Object} Calendar availability data
 */
export const fetchCalendarAvailability = async () => {
  try {
    
    // Fetch calendar rules
    const rulesDocRef = doc(db, 'calendar', 'rules');
    const rulesDoc = await getDoc(rulesDocRef);
    
    let rules = {
      timeSlots: {},
      weekDays: {},
      specialDates: []
    };
    
    if (rulesDoc.exists()) {
      const rulesData = rulesDoc.data();
      rules = {
        timeSlots: rulesData.timeSlots || {},
        weekDays: rulesData.weekDays || {},
        specialDates: rulesData.specialDates || []
      };
    }
    
    // Fetch existing bookings (both real and fake)
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const labelsCollectionRef = collection(db, 'calendar', 'data', 'labels');
    
    // Query for real bookings
    const realBookingQuery = query(
      labelsCollectionRef,
      where('label_type', '==', 'booking_real')
    );
    
    // Query for fake bookings
    const fakeBookingQuery = query(
      labelsCollectionRef,
      where('label_type', '==', 'booking_fake')
    );
    
    // Query for unavailable labels
    const unavailableQuery = query(
      labelsCollectionRef,
      where('label_type', '==', 'unavailable_manual')
    );
    
    // Execute all queries
    const [realBookingSnapshot, fakeBookingSnapshot, unavailableSnapshot] = await Promise.all([
      getDocs(realBookingQuery),
      getDocs(fakeBookingQuery),
      getDocs(unavailableQuery)
    ]);
    
    // Helper function to process documents - Firestore handles timezone conversion automatically
    const processDocuments = (snapshot) => {
      return snapshot.docs.map(doc => {
        const data = doc.data();
        // Firestore automatically converts UTC timestamps to local timezone when using toDate()
        const processed = {
          id: doc.id,
          ...data,
          label_date: data.label_date && typeof data.label_date.toDate === 'function' 
            ? data.label_date.toDate() // Firestore handles UTC to local conversion
            : data.label_date || null,
        };
        
        return processed;
      });
    };
    
    // Process and filter by date (keep only future dates)
    const filterByFutureDate = (labels) => {
      return labels.filter(label => {
        if (!label.label_date) return false;
        // Ensure label_date is a Date object before comparing
        const date = label.label_date instanceof Date 
          ? label.label_date 
          : new Date(label.label_date);
        
        // For comparison, we only care about the date part, not time
        const labelDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return labelDateOnly >= startOfToday;
      });
    };
    
    // Process real bookings
    const allRealBookings = processDocuments(realBookingSnapshot);
    const realBookings = filterByFutureDate(allRealBookings);
    
    // Process fake bookings
    const allFakeBookings = processDocuments(fakeBookingSnapshot);
    const fakeBookings = filterByFutureDate(allFakeBookings);
    
    // Process unavailable labels
    const allUnavailableLabels = processDocuments(unavailableSnapshot);
    const filteredUnavailableLabels = filterByFutureDate(allUnavailableLabels);
    const unavailableDays = filteredUnavailableLabels.filter(label => label.whole_day === true);
    
    // Combine real and fake bookings
    const bookings = [...realBookings, ...fakeBookings];
    
    return {
      rules,
      bookings,
      unavailableDays
    };
  } catch (error) {
    console.error("Error fetching calendar availability:", error);
    return {
      rules: {
        timeSlots: {},
        weekDays: {},
        specialDates: []
      },
      bookings: [],
      unavailableDays: []
    };
  }
};

/**
 * Create a booking with simplified timezone handling
 * Let Firestore handle all timezone conversions automatically
 */
export const createBooking = async (bookingData) => {
  try {
    const { date, time, notes = '' } = bookingData;
    
    if (!date || !time) {
      throw new Error('Date and time are required for booking');
    }

    // Parse the selected hour from the time string
    const [hours] = time.split(':').map(Number);
    
    // Create the booking date and time in local timezone (what user sees)
    const localDateTime = new Date(date);
    localDateTime.setHours(hours, 0, 0, 0);
    
    // Create a document ID with date and random hash
    const dateStr = date.toISOString().split('T')[0];
    const hash = Math.random().toString(36).substring(2, 7);
    const docId = `${dateStr}_booking_real_${hash}`;
    
    // Create the booking object - Firestore will handle UTC conversion automatically
    const newBooking = {
      timestamp: serverTimestamp(), // Creation time
      label_date: Timestamp.fromDate(localDateTime), // Firestore handles UTC conversion
      label_type: 'booking_real',
      whole_day: false,
      notes: notes || ''
    };
    
    // Add the document to Firestore
    const labelDocRef = doc(db, 'calendar', 'data', 'labels', docId);
    await setDoc(labelDocRef, newBooking);
    
    // Update the last_updated timestamp in the main document
    const calendarDataRef = doc(db, 'calendar', 'data');
    await updateDoc(calendarDataRef, {
      last_updated: serverTimestamp()
    });

    // Return the booking data for confirmation (with local time for display)
    return {
      id: docId,
      ...newBooking,
      label_date: localDateTime // Return local time for frontend display
    };
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};